import { CHANGE_PASSWORD_ERROR_CODE } from '@smartsash/auth';

// Map an `AuthError.messageKey` from a **sign-in attempt** onto a key that exists in admin's
// locale files (HS-859).
//
// `@smartsash/auth` reports failures as message KEYS, never as copy — the package owns no UI
// (03-05 §7). Admin used to render that key verbatim, so a bad password showed the literal
// string "errors.unauthorized". Anything unrecognised degrades to `errors.unknown` rather than
// leaking a key into the interface.
//
// Ported from `apps/portal/src/auth/errorMessages.ts`; the two apps deliberately share the key
// vocabulary even though their locale files are separate.
//
// HS-1165 (finding LE-01): the name now says `signIn` because that is the only thing this maps,
// and because one entry depends on it. The backend answers bad credentials with 401, which the
// package reports as `errors.unauthorized`, whose copy reads "Your session has expired. Please
// sign in again." On a sign-in attempt that is wrong twice over: the user has no session, and
// they are asked to do the thing they were already doing. In this context 401 means the
// credentials were rejected, so it maps to the login screen's own message. Everywhere else
// `errors.unauthorized` keeps its expired-session meaning — and `auth.error.sessionExpired`
// carries the identical string for callers that mean it.

const ERROR_TRANSLATIONS = {
  'auth.error.invalidSession': 'auth.error.invalidSession',
  'auth.error.passwordChangedSignInAgain': 'auth.error.passwordChangedSignInAgain',
  'auth.error.oauthFailed': 'auth.error.oauthFailed',
  'auth.error.oauthUrlMissing': 'auth.error.oauthUrlMissing',
  'auth.error.strategyDisabled': 'auth.error.strategyDisabled',
  'auth.error.unsupportedFlow': 'auth.error.unsupportedFlow',
  'auth.error.sessionExpired': 'auth.error.sessionExpired',
  'errors.network': 'errors.network',
  'errors.timeout': 'errors.timeout',
  'errors.unauthorized': 'login.errors.invalidCredentials',
  'errors.forbidden': 'errors.forbidden',
  'errors.notFound': 'errors.notFound',
  'errors.server': 'errors.server',
  'errors.unknown': 'errors.unknown',
  'errors.locked': 'login.errors.lockedTitle',
  'errors.tooManyRequests': 'login.errors.lockedTitle',
  // HS-1225: a sign-in rejected with 400 — which is what the backend answers when the submitted
  // value is not any account's `Login`, an email being the everyday example — normalizes to
  // `errors.validation`, whose copy is "Check the entered information and try again." That reads
  // as a complaint about the shape of the form and sends the user hunting for a typo in a field
  // that is spelled correctly. On a sign-in the only honest reading of a 400 is that these are
  // not usable credentials, so it says exactly what a 401 says. The mapping is scoped here rather
  // than in `keyForStatus`, which every other screen shares.
  'errors.validation': 'login.errors.invalidCredentials',
} as const;

/** The change-password screen's own vocabulary; see `changePasswordErrorTranslationKey`. */
const CHANGE_PASSWORD_TRANSLATIONS = {
  ...ERROR_TRANSLATIONS,
  // Only one credential is in play on that screen, and it is not a username.
  'errors.unauthorized': 'changePassword.errors.rejected',
  'errors.validation': 'changePassword.errors.rejected',
} as const;

/**
 * What the backend's `errorCode` means on this screen (HS-1405). The twin of
 * `apps/portal/src/auth/errorMessages.ts`.
 *
 * `PUT /api/User/change-password` answers both failures with a 400, so the status — and therefore
 * `messageKey` — is `errors.validation` either way, and a NEW password the backend refused was
 * reported as "The temporary password is incorrect."
 *
 * `policyViolation` also covers the backend's "old/new password is required", but the form's own
 * schema refuses both before a request is made, so what reaches here is the complexity rule.
 */
const CHANGE_PASSWORD_CODE_TRANSLATIONS: Record<string, AuthErrorTranslationKey> = {
  [CHANGE_PASSWORD_ERROR_CODE.policyViolation]: 'changePassword.errors.policy',
  [CHANGE_PASSWORD_ERROR_CODE.wrongCurrentPassword]: 'changePassword.errors.rejected',
};

export type AuthErrorTranslationKey =
  | (typeof ERROR_TRANSLATIONS)[keyof typeof ERROR_TRANSLATIONS]
  | (typeof CHANGE_PASSWORD_TRANSLATIONS)[keyof typeof CHANGE_PASSWORD_TRANSLATIONS]
  | 'changePassword.errors.policy';

function translate(
  table: Record<string, AuthErrorTranslationKey>,
  messageKey: string,
): AuthErrorTranslationKey {
  const translations: Partial<Record<string, AuthErrorTranslationKey>> = table;
  return translations[messageKey] ?? 'errors.unknown';
}

export function signInErrorTranslationKey(messageKey: string): AuthErrorTranslationKey {
  return translate(ERROR_TRANSLATIONS, messageKey);
}

/**
 * The same mapping for a **failed password change** (HS-1225).
 *
 * It used to borrow `signInErrorTranslationKey`, so a rejected temporary password was reported
 * as "Incorrect username or password" — on a screen with no username on it. The two flows fail
 * for different reasons and deserve different words.
 */
export function changePasswordErrorTranslationKey(
  messageKey: string,
  errorCode?: string,
): AuthErrorTranslationKey {
  // HS-1405: `errorCode` wins when the backend sent one — it is the only thing that separates the
  // two 400s. No code, or one this screen does not know, behaves exactly as it did before.
  const byCode = errorCode ? CHANGE_PASSWORD_CODE_TRANSLATIONS[errorCode] : undefined;
  return byCode ?? translate(CHANGE_PASSWORD_TRANSLATIONS, messageKey);
}
